import { useLocation, Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Home, Users, Briefcase, CalendarDays, BarChart3, ListTodo, UserCog,
  GraduationCap, HardHat, MapPin, Shield, TrendingUp, PoundSterling,
  MessageSquare, Wrench, Mail, ChevronDown, DoorOpen, UserPlus, Heart, LifeBuoy,
  LayoutDashboard, HeartPulse, BookOpen, Globe, Share2, Zap, Phone,
  ShieldBan, Database, Sparkles, HeartHandshake, ListChecks, Inbox,
  ClipboardList, Building2, Paintbrush, Filter, FileText, Upload, Eye,
  Image as ImageIcon, Megaphone, Settings, ClipboardCheck, Target,
} from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarMenu,
  SidebarMenuButton, SidebarMenuItem, useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import logo from "@/assets/evohome-logo.png";
import { useAdminSidebarCounts } from "@/hooks/useAdminSidebarCounts";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
}

interface NavGroup {
  label: string;
  description?: string;
  items: NavItem[];
}

/**
 * Admin sidebar — single master map of the platform, organised into the
 * canonical operator groups defined in the IA reset. Strict single-accordion
 * with 44px touch targets so iPad users never see two groups expand at once
 * and never lose their place across navigation.
 *
 * Every group has plain-English helper text so the operator can tell what
 * each area is for at a glance. No features are deleted — duplicated entries
 * that previously lived in dashboard pages are now contextual shortcuts only.
 */
const OWNER_EMAIL = "william@evohomeimprovements.co.uk";

const navGroups: NavGroup[] = [
  {
    label: "Run Today",
    description: "Your daily control room for what needs attention.",
    items: [
      { label: "Dashboard / Command Centre", href: "/portal/admin", icon: Home },
      { label: "My Work", href: "/portal/my-work", icon: ListChecks },

      { label: "Operational Queue", href: "/portal/admin/queue", icon: ListTodo },
      { label: "Approvals", href: "/portal/admin/approvals", icon: ListChecks },
    ],
  },
  {
    label: "Leads & Customers",
    description: "Manage enquiries, customers and homeowner accounts.",
    items: [
      { label: "Pipeline", href: "/portal/admin/pipeline", icon: ListTodo },
      { label: "Leads", href: "/portal/admin/leads", icon: Users },
      { label: "Contacts & before consultation", href: "/portal/marketing/contacts", icon: Users },
      { label: "Import contact lists", href: "/portal/marketing/import", icon: Upload },
      { label: "Recycle Pool / DATA", href: "/portal/admin/data-leads", icon: Filter },
      { label: "Customers", href: "/portal/admin/customers", icon: Heart },
      { label: "Planning prospects", href: "/portal/admin/planning-homeowners", icon: ClipboardList },
      { label: "Homeowner Accounts", href: "/portal/admin/members", icon: UserPlus },
      { label: "Requests & Complaints", href: "/portal/admin/support-cases", icon: LifeBuoy },
      { label: "Waitlist", href: "/portal/admin/waitlist", icon: ClipboardList },
      { label: "Remote Quotes", href: "/portal/admin/remote-quotes", icon: FileText },
      { label: "Disputes", href: "/portal/admin/disputes", icon: ShieldBan },
      { label: "Company Checks", href: "/portal/admin/company-checks", icon: Shield },
      { label: "Call Logs", href: "/portal/admin/call-logs", icon: Phone },
    ],
  },
  {
    label: "Projects & Jobs",
    description: "Manage jobs, appointments, surveys, aftercare and comms.",
    items: [
      { label: "Jobs / Projects", href: "/portal/admin/jobs", icon: Briefcase },
      { label: "Appointments", href: "/portal/admin/appointments", icon: CalendarDays },
      { label: "Blocked Dates", href: "/portal/admin/blocked-dates", icon: CalendarDays },
      { label: "Survey Operations", href: "/portal/admin/survey-operations", icon: ListTodo },
      { label: "Survey QA", href: "/portal/admin/survey-qa", icon: Shield },
      { label: "Aftercare", href: "/portal/admin/aftercare", icon: HeartHandshake },
      { label: "Comms Centre", href: "/portal/admin/comms", icon: Mail },
    ],
  },
  {
    label: "People & Recruitment",
    description: "Recruit, onboard and manage the people who help deliver the business.",
    items: [
      { label: "Recruitment Inbox", href: "/portal/admin/recruitment-inbox", icon: Inbox },
      { label: "Quick Apply", href: "/portal/admin/recruitment/quick-apply", icon: UserPlus },
      { label: "Installer Recruitment", href: "/portal/admin/installer-recruitment", icon: HardHat },
      { label: "Consultants", href: "/portal/admin/consultants", icon: UserCog },
      { label: "Surveyors / Specialists", href: "/portal/admin/surveyor-network", icon: Users },
      { label: "Installers", href: "/portal/admin/installers", icon: HardHat },
      { label: "Training & Compliance", href: "/portal/admin/training", icon: GraduationCap },
    ],
  },
  {
    label: "Partners & Suppliers",
    description: "Manage manufacturer, product, referral partners and territory coverage.",
    items: [
      { label: "Manufacturer Partners", href: "/portal/admin/partners", icon: Building2 },
      { label: "Referral / Business Partners", href: "/portal/admin/business-partners", icon: PoundSterling },
      { label: "Territory & Coverage", href: "/portal/admin/territory-control", icon: MapPin },
    ],
  },
  {
    label: "Content, SEO & Media",
    description: "One home for everything public-facing: pages, articles, case studies, gallery, SEO and knowledge.",
    items: [
      { label: "Content Hub (Overview)", href: "/portal/admin/content-hub", icon: LayoutDashboard },
      { label: "Homepage", href: "/portal/admin/content-hub?tab=homepage", icon: Home },
      { label: "Services", href: "/portal/admin/content-hub?tab=services", icon: Wrench },
      { label: "Blog & Articles", href: "/portal/admin/content-hub?tab=blog", icon: FileText },
      { label: "Premium Article Upgrades", href: "/portal/admin/content-hub?tab=premium-upgrades", icon: Sparkles },
      { label: "Case Studies", href: "/portal/admin/content-hub?tab=case-studies", icon: ImageIcon },
      { label: "Gallery / Projects", href: "/portal/admin/content-hub?tab=gallery", icon: ImageIcon },
      { label: "Marketing Candidates", href: "/portal/admin/content-hub?tab=marketing-candidates", icon: Inbox },
      { label: "Locations & SEO", href: "/portal/admin/content-hub?tab=locations-seo", icon: MapPin },
      { label: "Knowledge Hub", href: "/portal/admin/content-hub?tab=knowledge", icon: BookOpen },
      { label: "Brochures", href: "/portal/admin/content-hub?tab=brochures", icon: BookOpen },
      { label: "Documents", href: "/portal/admin/content-hub?tab=documents", icon: FileText },
      { label: "Content QA / Wording", href: "/portal/admin/content-hub?tab=content-qa", icon: Shield },
      { label: "FAQs", href: "/portal/admin/content-hub?tab=faqs", icon: FileText },
      { label: "About", href: "/portal/admin/content-hub?tab=about", icon: FileText },
      { label: "Legal", href: "/portal/admin/content-hub?tab=legal", icon: Shield },
    ],
  },
  {
    label: "Growth & Marketing",
    description: "SEO command centre, search insights, automation, reviews and outreach.",
    items: [
      { label: "Lead Generation", href: "/portal/admin/growth#leadgen", icon: Target },
      { label: "SEO / Growth Command Centre", href: "/portal/admin/growth", icon: TrendingUp },
      { label: "Google Search Console", href: "/portal/admin/growth#search", icon: Globe },
      { label: "SEO Opportunities", href: "/portal/admin/growth#opportunities", icon: Sparkles },
      { label: "Site Improvements", href: "/portal/admin/growth#site", icon: Wrench },
      { label: "Publishing Queue", href: "/portal/admin/growth#publish", icon: ListChecks },
      { label: "Site Health & SEO (PageSpeed / Lighthouse)", href: "/portal/admin/system-health#seo", icon: HeartPulse },
      { label: "Social & Reviews", href: "/portal/admin/social-reviews", icon: Share2 },
      { label: "Marketing Automation", href: "/portal/admin/automation", icon: Zap },
      { label: "Marketing Lead Upload", href: "/portal/marketing/lead-upload", icon: Upload },
      { label: "Competitor Exclusions", href: "/portal/admin/competitor-exclusions", icon: ShieldBan },
    ],
  },
  {
    label: "Business Development",
    description: "Outbound prospect database, campaigns, propositions and suppression.",
    items: [
      { label: "BD Overview", href: "/portal/admin/outbound", icon: LayoutDashboard },
      { label: "Prospect Companies", href: "/portal/admin/outbound/companies", icon: Building2 },
      { label: "Approval Queue", href: "/portal/admin/outbound/review", icon: ClipboardCheck },
      { label: "Outbound Campaigns", href: "/portal/admin/outbound/campaigns", icon: Megaphone },
      { label: "Planning Opportunities", href: "/portal/admin/outbound/planning", icon: MapPin },
      { label: "Suppression List", href: "/portal/admin/outbound/suppression", icon: ShieldBan },
      { label: "Outbound Configuration", href: "/portal/admin/outbound/config", icon: Settings },
    ],
  },
  {
    label: "AI Workbench",
    description: "AI-assisted drafting, approvals and conversation logs.",
    items: [
      { label: "AI Workbench (Today)", href: "/portal/admin/growth#today", icon: Sparkles },
      { label: "AI Conversations", href: "/portal/admin/ai-conversations", icon: MessageSquare },
      { label: "AI Adviser Settings", href: "/portal/admin/ai-settings", icon: Settings },
    ],
  },
  // (Knowledge Hub and Document Intelligence are now surfaced under
  // "Content, SEO & Media" — the old standalone routes still work.)
  {
    label: "Money & Commercial",
    description: "Manage pricing, payments, fees, commercial controls and compliance.",
    items: [
      { label: "Commercial Operations", href: "/portal/admin/commercial-operations", icon: PoundSterling },
      { label: "Pricing Governance", href: "/portal/admin/pricing-governance", icon: PoundSterling },
      { label: "Survey Fee Schedule", href: "/portal/admin/survey-fee-schedule", icon: PoundSterling },
      { label: "Installer Payouts", href: "/portal/admin/payouts", icon: PoundSterling },
      { label: "Contracts & Documents", href: "/portal/admin/contracts", icon: FileText },
      { label: "Compliance", href: "/portal/admin/compliance", icon: Shield },
    ],
  },
  {
    label: "Reports & Intelligence",
    description: "Understand performance and trends.",
    items: [
      { label: "Reporting", href: "/portal/admin/reporting", icon: BarChart3 },
      { label: "Customer Insights", href: "/portal/admin/customers/insights", icon: BarChart3 },
      { label: "Introducer Intelligence", href: "/portal/admin/introducer-intelligence", icon: DoorOpen },
      { label: "Introducers (Manage)", href: "/portal/introducer-manager/introducers", icon: Users },
      { label: "Installer Intelligence", href: "/portal/admin/installer-intelligence", icon: TrendingUp },
    ],
  },
  {
    label: "Site Configuration",
    description: "Global site settings, navigation, social links and reusable templates. Email templates live under Comms Centre (Projects & Jobs).",
    items: [
      { label: "Site Settings", href: "/portal/admin/site-configuration?tab=settings", icon: Wrench },
      { label: "Navigation Menus", href: "/portal/admin/site-configuration?tab=navigation", icon: LayoutDashboard },
      { label: "Social Links", href: "/portal/admin/site-configuration?tab=social", icon: Share2 },
      { label: "Quote Pack Templates", href: "/portal/admin/quote-pack-templates", icon: FileText },
    ],
  },

];

// Owner navigation uses six working areas. Less frequent controls remain in settings.
const groupItems = (...labels: string[]) => navGroups.filter(g => labels.includes(g.label)).flatMap(g => g.items);
const ownerGroups: NavGroup[] = [
  { label: "Today", description: "Appointments, tasks and decisions that need you.", items: groupItems("Run Today") },
  { label: "Customers & enquiries", description: "Enquiries, customer records and support.", items: groupItems("Leads & Customers") },
  { label: "Projects & quotes", description: "Project progress, visits, surveys and aftercare.", items: groupItems("Projects & Jobs") },
  { label: "Installers & partners", description: "Installer, specialist and business relationships.", items: groupItems("People & Recruitment", "Partners & Suppliers") },
  { label: "Marketing", description: "Lead generation, content, search and outreach.", items: [
    ...groupItems("Growth & Marketing"),
    { label: "Content, articles & media", href: "/portal/admin/content-hub", icon: BookOpen },
    ...groupItems("Business Development"),
    ...groupItems("AI Workbench").filter(i => i.href.includes("ai-conversations")),
  ] },
  { label: "Money & reports", description: "Commercial work, payments and performance.", items: groupItems("Money & Commercial", "Reports & Intelligence") },
  { label: "Settings & content tools", description: "Site setup, individual content editors and AI controls.", items: [
    ...groupItems("Site Configuration"),
    ...groupItems("Content, SEO & Media").filter(i => i.href !== "/portal/admin/content-hub"),
    ...groupItems("AI Workbench").filter(i => i.href.includes("ai-settings")),
  ] },
];

/** Director-only — keeps platform settings, security and dev surfaces away
 * from normal admins. Surfaced only when the authenticated user's email
 * matches OWNER_EMAIL. */
const directorOnlyGroup: NavGroup = {
  label: "Access & testing",
  description: "Manage platform settings, access, health and security.",
  items: [
    { label: "Admin Users", href: "/portal/admin/users", icon: Users },
    { label: "Security", href: "/portal/admin/security", icon: Shield },
    { label: "Feature Flags", href: "/portal/admin/feature-flags", icon: Wrench },
    { label: "System Health", href: "/portal/admin/system-health", icon: HeartPulse },
    { label: "Data Cleanup", href: "/portal/admin/data-cleanup", icon: Database },
    { label: "Design Lab", href: "/portal/consultant/toolkit", icon: Paintbrush },
    { label: "Design tool tests", href: "/portal/admin/design-lab", icon: Paintbrush },
  ],
};

const OPEN_GROUP_KEY = "evohome.admin.sidebar.openGroup";
const DEFAULT_OPEN_GROUP = "Today";

const getPath = (href: string) => href.split("#")[0].split("?")[0];

export default function AdminPortalSidebar() {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [isDirector, setIsDirector] = useState(false);
  const sidebarCounts = useAdminSidebarCounts();

  // Director check: surface the System & Security group only to the owner email.
  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (active) setIsDirector(user?.email?.toLowerCase() === OWNER_EMAIL);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setIsDirector(session?.user?.email?.toLowerCase() === OWNER_EMAIL);
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);

  const visibleGroups = useMemo(
    () => (isDirector ? [...ownerGroups, directorOnlyGroup] : ownerGroups),
    [isDirector],
  );

  // Find the group that owns the currently active route (if any).
  const activeGroupLabel = useMemo(() => {
    const exactHref = location.pathname + location.search + location.hash;
    const found = visibleGroups.find(g => g.items.some(i => i.href === exactHref)) ??
      visibleGroups.find(g => g.items.some(i => getPath(i.href) === location.pathname));
    return found?.label ?? null;
  }, [location.pathname, location.search, location.hash, visibleGroups]);

  // Single openGroup state — persisted across reloads. Active route always wins.
  const [openGroup, setOpenGroup] = useState<string>(() => {
    if (typeof window === "undefined") return DEFAULT_OPEN_GROUP;
    try {
      return localStorage.getItem(OPEN_GROUP_KEY) || DEFAULT_OPEN_GROUP;
    } catch {
      return DEFAULT_OPEN_GROUP;
    }
  });

  // When the user navigates into another group, follow them automatically.
  useEffect(() => {
    if (activeGroupLabel && activeGroupLabel !== openGroup) {
      setOpenGroup(activeGroupLabel);
    }
  }, [activeGroupLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist last user choice.
  useEffect(() => {
    try { localStorage.setItem(OPEN_GROUP_KEY, openGroup); } catch { /* noop */ }
  }, [openGroup]);

  useEffect(() => {
    let active = true;
    const fetchCount = async () => {
      const { count } = await supabase
        .from("leads")
        .select("id", { count: "exact", head: true })
        .in("status", ["new", "new_enquiry"])
        .is("deleted_at", null)
        .not("lead_category", "eq", "data")
        .eq("is_test", false);
      if (active) setNewLeadCount(count ?? 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const isActive = (href: string) => {
    const [beforeQuery, query] = href.split("?");
    const [path, hash] = beforeQuery.split("#");
    if (location.pathname !== path) return false;
    if (hash) {
      const currentHash = location.hash.replace("#", "");
      return currentHash === hash;
    }
    if (!query) {
      // Parent link is active only when no tab param / hash is set.
      if (path === "/portal/admin/pipeline") {
        return !location.search.includes("tab=");
      }
      if (path === "/portal/admin/growth" || path === "/portal/admin/system-health") {
        return !location.hash;
      }
      return true;
    }
    const targetTab = new URLSearchParams(query).get("tab");
    const currentTab = new URLSearchParams(location.search).get("tab");
    return targetTab === currentTab;
  };
  const handleNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarContent className="gap-0 [scrollbar-width:thin]">
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <img
            src={logo}
            alt="EvoHome Improvements"
            className={cn("object-contain transition-all", collapsed ? "h-8 w-8" : "h-10")}
          />
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground leading-tight truncate">
                EvoHome Improvements
              </p>
              <p className="text-[10px] text-muted-foreground leading-tight">Owner workspace</p>
            </div>
          )}
        </div>

        {visibleGroups.map((group) => {
          const badgeFor = (href: string): number | undefined => {
            if (href === "/portal/admin/pipeline" && newLeadCount > 0) return newLeadCount;
            if (href === "/portal/admin/recruitment-inbox" && sidebarCounts.recruitmentInbox != null && sidebarCounts.recruitmentInbox > 0) return sidebarCounts.recruitmentInbox;
            if (href === "/portal/admin/disputes" && sidebarCounts.disputesOpen != null && sidebarCounts.disputesOpen > 0) return sidebarCounts.disputesOpen;
            if (href === "/portal/admin/members" && sidebarCounts.membersUnconverted != null && sidebarCounts.membersUnconverted > 0) return sidebarCounts.membersUnconverted;
            if (href === "/portal/admin/waitlist" && sidebarCounts.waitlist != null && sidebarCounts.waitlist > 0) return sidebarCounts.waitlist;
            if (href === "/portal/admin/remote-quotes" && sidebarCounts.remoteQuotesNew != null && sidebarCounts.remoteQuotesNew > 0) return sidebarCounts.remoteQuotesNew;
            if (href === "/portal/admin/approvals" && sidebarCounts.variationApprovalsPending != null && sidebarCounts.variationApprovalsPending > 0) return sidebarCounts.variationApprovalsPending;
            return undefined;
          };
          const enriched = {
            ...group,
            items: group.items.map((item) => {
              const b = badgeFor(item.href);
              return b != null ? { ...item, badge: b } : item;
            }),
          };
          return (
            <NavGroupSection
              key={group.label}
              group={enriched}
              collapsed={collapsed}
              isOpen={openGroup === group.label}
              isActiveGroup={activeGroupLabel === group.label}
              isActive={isActive}
              onToggle={() =>
                setOpenGroup((prev) => (prev === group.label ? "" : group.label))
              }
              onNavigate={handleNavigate}
            />
          );
        })}
        {Object.values(sidebarCounts).some(value => value === null) && !collapsed && <p role="status" className="px-3 py-2 text-xs text-muted-foreground">Some work counts could not be read. Open the relevant page to check.</p>}
      </SidebarContent>
    </Sidebar>
  );
}

function NavGroupSection({
  group, collapsed, isOpen, isActiveGroup, isActive, onToggle, onNavigate,
}: {
  group: NavGroup;
  collapsed: boolean;
  isOpen: boolean;
  isActiveGroup: boolean;
  isActive: (href: string) => boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  if (collapsed) {
    return (
      <SidebarGroup className="py-1 border-t border-border first:border-t-0">
        <SidebarMenu>
          {group.items.map((item) => (
            <SidebarMenuItem key={item.href}>
              <SidebarMenuButton
                asChild
                isActive={isActive(item.href)}
                tooltip={item.label}
                className="text-sm min-h-[44px] touch-manipulation"
              >
                <Link to={item.href} onClick={onNavigate}>
                  <item.icon className="h-4 w-4" />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup className="py-1 border-t border-border first:border-t-0">
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex w-full items-center justify-between px-3 py-2 min-h-[44px] text-xs font-semibold uppercase tracking-wider rounded-md transition-colors touch-manipulation select-none",
              "text-muted-foreground hover:text-foreground active:bg-muted/60",
              isActiveGroup && "text-primary"
            )}
            aria-expanded={isOpen}
          >
            <span>{group.label}</span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarGroupContent>
            {group.description && (
              <p className="px-3 pt-1 pb-2 text-[11px] leading-snug text-muted-foreground">
                {group.description}
              </p>
            )}
            <SidebarMenu>
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      className={cn(
                        "text-sm h-auto min-h-[44px] touch-manipulation",
                        active && "bg-primary/10 text-primary border-l-2 border-primary"
                      )}
                    >
                      <Link to={item.href} onClick={onNavigate} aria-current={active ? "page" : undefined}>
                        <item.icon className="h-4 w-4" />
                        <span className="whitespace-normal leading-snug flex-1">{item.label}</span>
                        {item.badge != null && item.badge > 0 && (
                          <Badge
                            variant="destructive"
                            className="ml-auto text-[10px] px-1.5 py-0 h-5 min-w-[20px] justify-center"
                          >
                            {item.badge > 99 ? "99+" : item.badge}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
}
